import { Helmet } from 'react-helmet-async';

// Default SEO configuration for AMR Global Holdings LLC
export const defaultSEO = {
  siteName: "AMR Global Holdings LLC",
  defaultTitle: "AMR Global Holdings LLC | Veteran-Owned Aviation Infrastructure",
  defaultDescription: "Veteran-Owned aviation infrastructure company based in New Orleans, Louisiana. Aircraft brokerage, aviation logistics, mission-ready support nationwide.",
  siteUrl: "https://amrglobalholdings.com",
  defaultImage: "https://images.unsplash.com/photo-1540962351504-03099e0a754b?w=1200&h=630&fit=crop",
  twitterHandle: "@amrglobal",
  locale: "en_US",
};

// Page-specific SEO configurations
export const pageSEO = {
  home: {
    title: "Veteran-Owned Aviation Infrastructure Company | AMR Global Holdings LLC",
    description: "AMR Global Holdings LLC is a Veteran-Owned aviation infrastructure company based in New Orleans, Louisiana. Aircraft brokerage, aviation logistics, and mission-ready support nationwide.",
    keywords: "veteran owned aviation, aircraft brokerage, aviation logistics, New Orleans aviation, Louisiana aviation company, mission ready aviation",
  },
  about: {
    title: "About Us | AMR Global Holdings LLC",
    description: "Learn about AMR Global Holdings LLC, a Veteran-Owned aviation infrastructure company headquartered in New Orleans, Louisiana. Mission-ready aviation support nationwide.",
    keywords: "about AMR Global, veteran owned business, New Orleans aviation company, aviation infrastructure",
  },
  services: {
    title: "Aviation Services | AMR Global Holdings LLC",
    description: "Comprehensive aviation services from AMR Global Holdings LLC. Aircraft support, logistics, aerial photography, and mission-ready operations. New Orleans, Louisiana.",
    keywords: "aviation services, aircraft support, aviation logistics, New Orleans aviation services",
  },
  aircraftBrokerage: {
    title: "Aircraft & Jet Brokerage Services | AMR Global Holdings LLC",
    description: "Veteran-Owned aircraft brokerage and acquisition support for jets, cargo aircraft, helicopters, amphibious and special mission platforms. New Orleans, nationwide.",
    keywords: "aircraft broker, jet brokerage, aircraft sourcing, cargo aircraft, helicopter broker, amphibious aircraft, government aviation support, New Orleans, Louisiana",
  },
  medicalDelivery: {
    title: "Critical Medical Delivery | AMR Global Holdings LLC",
    description: "Time-sensitive delivery coordination and logistics support for critical missions. Air + ground routing support. Veteran-Owned. New Orleans, nationwide.",
    keywords: "time-sensitive delivery, critical delivery, medical logistics coordination, expedited delivery support, air ground routing, New Orleans, Louisiana",
  },
  missionSupport: {
    title: "Mission Support Services | AMR Global Holdings LLC",
    description: "Security, disaster response, and expeditionary operations support. Veteran-led teams with 72-hour mobilization. New Orleans, Louisiana. Nationwide deployment.",
    keywords: "security services, disaster response, expeditionary logistics, veteran security, FEMA support, DoD contractor, New Orleans",
  },
  aircraftDetailing: {
    title: "Aircraft Detailing & Readiness | AMR Global Holdings LLC",
    description: "Military-precision aircraft detailing and aviation readiness services for private jets, charter fleets, and government aircraft. New Orleans Lakefront Airport.",
    keywords: "aircraft detailing, jet detailing, aviation readiness, aircraft cleaning, New Orleans aircraft services",
  },
  aviation: {
    title: "Aviation Operations | AMR Global Holdings LLC",
    description: "Aviation readiness and airfield operations support. Aircraft support, ground ops, ferry coordination. Based at New Orleans Lakefront Airport (KNEW).",
    keywords: "aviation operations, airfield support, aircraft support, New Orleans airport, KNEW airport",
  },
  logistics: {
    title: "Logistics & Rapid Response | AMR Global Holdings LLC",
    description: "Rapid response and critical delivery logistics with 72-hour mobilization. Time-sensitive transport. Veteran-Owned. New Orleans, Louisiana.",
    keywords: "rapid response logistics, critical delivery, time sensitive transport, New Orleans logistics",
  },
  aerialPhotography: {
    title: "Aerial Photography & Drone Operations | AMR Global Holdings LLC",
    description: "Government-grade aerial imaging and ISR flight operations. Infrastructure photography, site surveys, drone services. New Orleans, Louisiana.",
    keywords: "aerial photography, drone operations, ISR services, infrastructure photography, New Orleans drone services",
  },
  governmentContracting: {
    title: "Government Contracting | AMR Global Holdings LLC",
    description: "Government contractor ready. SAM.gov registered, CAGE Code active, HUBZone-aligned. Veteran-Owned aviation and logistics support. New Orleans, Louisiana.",
    keywords: "government contractor, federal contractor, SAM registered, CAGE code, HUBZone, veteran owned contractor, New Orleans",
  },
  certifications: {
    title: "Certifications & Compliance | AMR Global Holdings LLC",
    description: "Government contractor certifications and compliance. Veteran-Owned, SAM.gov registered, CAGE Code active, HUBZone-aligned. New Orleans, Louisiana.",
    keywords: "contractor certifications, SAM registration, CAGE code, veteran owned certification, compliance",
  },
  webServices: {
    title: "Web Services | AMR Digital Infrastructure | AMR Global Holdings LLC",
    description: "Professional websites for small businesses and contractors. Done-for-you website design with hosting. Veteran-Owned. New Orleans, Louisiana.",
    keywords: "web design, business websites, contractor websites, veteran owned web services, New Orleans web design",
  },
  contact: {
    title: "Contact AMR Global Holdings LLC",
    description: "Contact AMR Global Holdings LLC in New Orleans, Louisiana. Veteran-Owned aviation infrastructure company. Aircraft brokerage and mission-ready support.",
    keywords: "contact AMR Global, New Orleans aviation company, veteran owned aviation contact",
  },
};

// SEO Head Component
export const SEOHead = ({ 
  title, 
  description, 
  keywords,
  image,
  url,
  type = "website",
  noIndex = false 
}) => {
  const pageTitle = title || defaultSEO.defaultTitle;
  const pageDescription = description || defaultSEO.defaultDescription;
  const pageImage = image || defaultSEO.defaultImage;
  const pageUrl = url || defaultSEO.siteUrl;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="title" content={pageTitle} />
      <meta name="description" content={pageDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      
      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      
      {/* Canonical URL */}
      <link rel="canonical" href={pageUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:site_name" content={defaultSEO.siteName} />
      <meta property="og:locale" content={defaultSEO.locale} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={pageUrl} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />
      
      {/* Additional */}
      <meta name="author" content="AMR Global Holdings LLC" />
      <meta name="geo.region" content="US-LA" />
      <meta name="geo.placename" content="New Orleans" />
    </Helmet>
  );
};

// Related Services Component for internal linking
export const RelatedServices = ({ currentPage }) => {
  const allServices = [
    { name: "Aircraft Brokerage", path: "/aircraft-brokerage", description: "Jet & aircraft acquisition support" },
    { name: "Critical Medical Delivery", path: "/medical-delivery", description: "Time-sensitive logistics" },
    { name: "Mission Support", path: "/mission-support", description: "Security & disaster response" },
    { name: "Aircraft Detailing", path: "/aircraft-detailing", description: "Aviation readiness services" },
    { name: "Aviation Operations", path: "/aviation", description: "Airfield & flight support" },
    { name: "Logistics", path: "/logistics", description: "Rapid response delivery" },
    { name: "Aerial Photography", path: "/aerial-photography", description: "Drone & ISR services" },
    { name: "Government Contracting", path: "/government-contracting", description: "Federal contractor support" },
    { name: "Contact", path: "/contact", description: "Get in touch" },
  ];

  // Filter out current page and limit to 4 related services
  const relatedServices = allServices
    .filter(service => service.path !== currentPage)
    .slice(0, 4);

  return (
    <section className="py-12 bg-slate-50 border-t border-slate-200" data-testid="related-services">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-heading text-xl text-[#1a365d] mb-6 text-center">RELATED SERVICES</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {relatedServices.map((service, index) => (
            <a
              key={index}
              href={service.path}
              className="p-4 bg-white border border-slate-200 rounded-sm hover:border-[#c5a04a] transition-colors text-center"
            >
              <p className="font-heading text-sm text-[#1a365d] mb-1">{service.name}</p>
              <p className="text-slate-500 text-xs">{service.description}</p>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

// Schema markup for LocalBusiness
export const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "AMR Global Holdings LLC",
  "description": "Veteran-Owned aviation infrastructure company providing aircraft brokerage, aviation logistics, and mission-ready support.",
  "url": "https://amrglobalholdings.com",
  "telephone": "+1-504-319-7683",
  "email": "AMREID@AMRGLOBALHOLDINGS.COM",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "6001 Stars and Stripes Blvd, Suite 244",
    "addressLocality": "New Orleans",
    "addressRegion": "LA",
    "postalCode": "70126",
    "addressCountry": "US"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "30.0424",
    "longitude": "-90.0284"
  },
  "areaServed": [
    {
      "@type": "City",
      "name": "New Orleans"
    },
    {
      "@type": "State",
      "name": "Louisiana"
    },
    {
      "@type": "Country",
      "name": "United States"
    }
  ],
  "priceRange": "$$$$",
  "openingHours": "Mo-Su 00:00-23:59",
  "sameAs": [],
  "image": "https://customer-assets.emergentagent.com/job_rapid-deploy-52/artifacts/omrsi8tb_1000028432.jpg"
};

// Organization schema
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "AMR Global Holdings LLC",
  "alternateName": "AMR Global",
  "url": "https://amrglobalholdings.com",
  "logo": "https://customer-assets.emergentagent.com/job_rapid-deploy-52/artifacts/omrsi8tb_1000028432.jpg",
  "description": "Veteran-Owned aviation infrastructure company based in New Orleans, Louisiana.",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "6001 Stars and Stripes Blvd, Suite 244",
    "addressLocality": "New Orleans",
    "addressRegion": "LA",
    "postalCode": "70126",
    "addressCountry": "US"
  },
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+1-504-319-7683",
    "contactType": "customer service",
    "email": "AMREID@AMRGLOBALHOLDINGS.COM",
    "availableLanguage": "English"
  },
  "founder": {
    "@type": "Person",
    "name": "Alexander M. Reid"
  },
  "foundingLocation": {
    "@type": "Place",
    "name": "New Orleans, Louisiana"
  }
};

export default SEOHead;
